/** Wervingsmodule — API-laag (/Api/werving: engine-voordeur op develop, Node-doorgeefluik op main; zelfde paden). */
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// Eigen kopie van het fetch-hulpje (main heeft geen Verbeterregister), zodat dit bestand op beide branches gelijk is.
// Fouten: `detail` (engine) of `error` (Node) als melding.
async function vraag(pad, opties = {}) {
  const r = await authFetch(`${BACKEND_URL}${pad}`, {
    ...opties,
    headers: { 'Content-Type': 'application/json', ...(opties.headers || {}) },
    body: opties.body !== undefined ? JSON.stringify(opties.body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fout = new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
    fout.status = r.status;
    throw fout;
  }
  return d;
}

export const wervingApi = {
  overzicht: () => vraag('/Api/werving/overzicht'),
  vacature: (id) => vraag(`/Api/werving/vacatures/${id}`),
  vacatureNieuw: (body) => vraag('/Api/werving/vacatures', { method: 'POST', body }),
  vacatureWijzig: (id, body) => vraag(`/Api/werving/vacatures/${id}`, { method: 'PATCH', body }),
  teKoppelen: () => vraag('/Api/werving/te-koppelen'),
  disciplines: () => vraag('/Api/werving/disciplines'),
  teamleadsZet: (sleutel, teamleads) => vraag(`/Api/werving/disciplines/${sleutel}`, { method: 'PUT', body: { teamleads } }),
  mensen: () => vraag('/Api/werving/mensen'),
  // Teams & teamleads (werving edit). Verwijderen: het scherm bevestigt eerst (bevestig), de server weigert
  // als er vacatures aan hangen (409 → archiveren).
  teams: () => vraag('/Api/werving/teams'),
  teamMaak: (body) => vraag('/Api/werving/teams', { method: 'POST', body }),
  teamWijzig: (sleutel, body) => vraag(`/Api/werving/teams/${encodeURIComponent(sleutel)}`, { method: 'PATCH', body }),
  teamVerwijder: (sleutel) => vraag(`/Api/werving/teams/${encodeURIComponent(sleutel)}`, { method: 'DELETE' }),
  teamsVolgorde: (volgorde) => vraag('/Api/werving/teams/volgorde', { method: 'PUT', body: { volgorde } }),
  teamsMeldingGezien: () => vraag('/Api/werving/teams/melding-gezien', { method: 'POST' }),
  leesrecht: (sleutel, email) => vraag(`/Api/werving/teams/${encodeURIComponent(sleutel)}/leesrecht`, { method: 'POST', body: { email } }),
  kaart: (id) => vraag(`/Api/werving/sollicitaties/${id}`),
  beoordeel: (id, body) => vraag(`/Api/werving/sollicitaties/${id}/beoordeling`, { method: 'POST', body }),
  notitie: (id, tekst) => vraag(`/Api/werving/sollicitaties/${id}/notities`, { method: 'POST', body: { tekst } }),
  fase: (id, fase, verwacht) => vraag(`/Api/werving/sollicitaties/${id}/fase`, { method: 'PATCH', body: { fase, verwacht } }),
  gesprek: (id, gesprekOp) => vraag(`/Api/werving/sollicitaties/${id}/gesprek`, { method: 'PATCH', body: { gesprek_op: gesprekOp } }),
  toestemming: (id, waarde) => vraag(`/Api/werving/sollicitaties/${id}/toestemming`, { method: 'PATCH', body: { toestemming: waarde } }),
  koppel: (id, vacatureId) => vraag(`/Api/werving/sollicitaties/${id}/vacature`, { method: 'PATCH', body: { vacature_id: vacatureId } }),
  concept: (id, sjabloonId) => vraag(`/Api/werving/sollicitaties/${id}/concept`, { method: 'POST', body: { sjabloon_id: sjabloonId } }),
  brief: (id) => vraag(`/Api/werving/sollicitaties/${id}/brief`),
  // Mailwisseling uit ikwilwerken@ op verzoek (read-only, niets opgeslagen; inzage komt in de audit).
  correspondentie: (id) => vraag(`/Api/werving/sollicitaties/${id}/correspondentie`),
  // Stagetypes (beheer zoals de teams; werving edit). Verwijderen: StagetypesBeheer bevestigt eerst (bevestig),
  // de server weigert als er sollicitaties aan hangen (409 → archiveren).
  stagetypes: () => vraag('/Api/werving/stagetypes'),
  stagetypeMaak: (body) => vraag('/Api/werving/stagetypes', { method: 'POST', body }),
  stagetypeWijzig: (sleutel, body) => vraag(`/Api/werving/stagetypes/${encodeURIComponent(sleutel)}`, { method: 'PATCH', body }),
  stagetypeVerwijder: (sleutel) => vraag(`/Api/werving/stagetypes/${encodeURIComponent(sleutel)}`, { method: 'DELETE' }),
  stagetypesVolgorde: (volgorde) => vraag('/Api/werving/stagetypes/volgorde', { method: 'PUT', body: { volgorde } }),
  herindeling: () => vraag('/Api/werving/stagetypes/herindeling'),
  herindelingToepassen: (toewijzingen) => vraag('/Api/werving/stagetypes/herindeling', { method: 'POST', body: { toewijzingen } }),
  // Kaart: stagetype en antwoordadres handmatig (vastgelegd als 'mens'; de AI draait het niet terug).
  stagetype: (id, stagetype) => vraag(`/Api/werving/sollicitaties/${id}/stagetype`, { method: 'PATCH', body: { stagetype } }),
  antwoordadres: (id, adres) => vraag(`/Api/werving/sollicitaties/${id}/antwoordadres`, { method: 'PATCH', body: { antwoordadres: adres } }),
  // Wekelijkse herinnering: stand + wie er nu in zou staan (niets versturen).
  herinnering: () => vraag('/Api/werving/herinnering'),
};

/** Bijlage als blob (pdf inline tonen); de server stuurt no-store en slaat niets op. */
export async function haalBijlage(id, index) {
  return haalBlob(`${BACKEND_URL}/Api/werving/sollicitaties/${id}/bijlagen/${index}`);
}

/** Bijlage uit een bericht van de correspondentie (alleen uit hetzelfde gesprek; zelfde proxy, no-store). */
export async function haalCorrespondentieBijlage(id, berichtId, bijlageId) {
  return haalBlob(`${BACKEND_URL}/Api/werving/sollicitaties/${id}/correspondentie/${encodeURIComponent(berichtId)}`
    + `/bijlagen/${encodeURIComponent(bijlageId)}`);
}

async function haalBlob(url) {
  const r = await authFetch(url);
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    const fout = new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
    fout.status = r.status;
    throw fout;
  }
  return r.blob();
}
